import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Building2, Calendar, ChevronDown, Clock,
    Map, MapPin, Stethoscope, Users, Navigation,
} from "lucide-react";
import { GoogleMap, Marker, Polyline, InfoWindow, useJsApiLoader } from "@react-google-maps/api";
import PageLayout from "../../components/PageLayout";
import config from "../../config";
import { useAuth } from "../../context/AuthContext";
import { getAllEmployees } from "../../api/employee";
import { getVisitTracker } from "../../api/report";
import { fmtTime } from "../../utils/datetime";

// ── Types ─────────────────────────────────────────────────────────────────────

interface Employee { id: number; name: string; employee_code?: string; }

interface TrackedVisit {
    visit_id: number;
    visit_type: "doctor" | "firm";
    entity_name: string;
    entity_code: string | null;
    employee_name: string;
    check_in: string | null;
    check_out: string | null;
    latitude: number | null;
    longitude: number | null;
    sequence: number;
}

interface VisitTrackerData {
    date: string;
    employee_name?: string;
    total_visits: number;
    doctor_visits: number;
    firm_visits: number;
    distance_km?: number | null;
    visits: TrackedVisit[];
}

interface Props { onLogout: () => void; onNavigate: (page: string) => void; }

// ── Constants ─────────────────────────────────────────────────────────────────

const MAPS_KEY = config.googleMapsApiKey;

const DOCTOR_COLOR = "#3B82F6";   // blue
const FIRM_COLOR   = "#A855F7";   // purple
const START_COLOR  = "#22C55E";   // green
const END_COLOR    = "#EF4444";   // red
const LINE_COLOR   = "#3B82F6";

const MAP_STYLE: React.CSSProperties = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: 22.5, lng: 80.0 };
const DEFAULT_ZOOM = 5;

// ── Helpers ───────────────────────────────────────────────────────────────────

const today = () => new Date().toISOString().split("T")[0];
const visitColor = (type: string) => type === "doctor" ? DOCTOR_COLOR : FIRM_COLOR;

// ── Pin SVG Factory ────────────────────────────────────────────────────────────

const makePinSvg = (label: string, fill: string, ring: string) => {
    const size = 40;
    const r = 16;
    const cx = 20;
    const cy = 18;
    const tail = 10;
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size + tail}" viewBox="0 0 ${size} ${size + tail}">
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="${fill}" stroke="${ring}" stroke-width="3.5"/>
        <text x="${cx}" y="${cy + 5}" text-anchor="middle" fill="white" font-size="13" font-weight="900" font-family="Arial,sans-serif">${label}</text>
        <polygon points="${cx - 5},${size} ${cx + 5},${size} ${cx},${size + tail}" fill="${fill}"/>
    </svg>`;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color }: { icon: React.ElementType; label: string; value: string | number; sub?: string; color: string }) {
    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4">
            <div className={`p-3 rounded-xl ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                {sub && <p className="text-xs text-gray-400">{sub}</p>}
            </div>
        </div>
    );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function VisitTrackerPage({ onLogout, onNavigate }: Props) {
    const { user } = useAuth();
    const tenantId = (user as any)?.tenant_id as number;

    const { isLoaded } = useJsApiLoader({ id: "gmap", googleMapsApiKey: MAPS_KEY });

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [selectedEmp, setSelectedEmp] = useState<number | null>(null);
    const [date, setDate] = useState(today());
    const [data, setData] = useState<VisitTrackerData | null>(null);
    const [loading, setLoading] = useState(false);
    const [activeVisit, setActiveVisit] = useState<TrackedVisit | null>(null);
    const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

    useEffect(() => {
        if (!tenantId) return;
        getAllEmployees(tenantId).then((list: any[]) =>
            setEmployees((Array.isArray(list) ? list : []).map(e => ({
                id: e.id,
                name: e.user?.name ?? e.name ?? e.employee_name ?? "",
                employee_code: e.employee_code ?? e.code,
            })))
        ).catch(() => {});
    }, [tenantId]);

    const fetchReport = async (d?: string, empId?: number | null) => {
        if (!tenantId) return;
        setLoading(true);
        setActiveVisit(null);
        try {
            const res = await getVisitTracker(tenantId, d ?? date, empId !== undefined ? empId : selectedEmp);
            const raw = res.data.data ?? res.data;
            const doctorList: any[] = raw.doctor_visits ?? [];
            const firmList: any[] = raw.firm_visits ?? [];

            const allVisits: TrackedVisit[] = [
                ...doctorList.map((v: any) => ({
                    visit_id: v.visit_id,
                    visit_type: 'doctor' as const,
                    entity_name: v.name ?? '',
                    entity_code: v.city ?? null,
                    employee_name: v.employee_name ?? '',
                    check_in: v.visit_from ?? null,
                    check_out: v.visit_to ?? null,
                    latitude: v.latitude ?? null,
                    longitude: v.longitude ?? null,
                    sequence: 0,
                })),
                ...firmList.map((v: any) => ({
                    visit_id: v.visit_id,
                    visit_type: 'firm' as const,
                    entity_name: v.name ?? '',
                    entity_code: v.city ?? null,
                    employee_name: v.employee_name ?? '',
                    check_in: v.visit_from ?? null,
                    check_out: v.visit_to ?? null,
                    latitude: v.latitude ?? null,
                    longitude: v.longitude ?? null,
                    sequence: 0,
                })),
            ].sort((a, b) => {
                if (!a.check_in) return 1;
                if (!b.check_in) return -1;
                return new Date(a.check_in).getTime() - new Date(b.check_in).getTime();
            });
            allVisits.forEach((v, i) => { v.sequence = i + 1; });

            setData({
                date: raw.date ?? (d ?? date),
                employee_name: raw.employee_name,
                total_visits: allVisits.length,
                doctor_visits: doctorList.length,
                firm_visits: firmList.length,
                visits: allVisits,
            });
        } catch {
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    // auto-load all employees' data when page first opens
    useEffect(() => { if (tenantId) fetchReport(date, null); }, [tenantId]);

    const onMapLoad = useCallback((map: google.maps.Map) => {
        setMapRef(map);
    }, []);

    const mapped = (data?.visits ?? []).filter(v => v.latitude != null && v.longitude != null);
    const polyline = mapped.map(v => ({ lat: v.latitude!, lng: v.longitude! }));

    useEffect(() => {
        if (!mapRef || mapped.length === 0) return;
        const bounds = new window.google.maps.LatLngBounds();
        mapped.forEach(v => bounds.extend({ lat: v.latitude!, lng: v.longitude! }));
        setTimeout(() => mapRef.fitBounds(bounds, 60), 300);
    }, [mapRef, data]);

    const startVisit = mapped[0] ?? null;
    const endVisit = mapped.length > 1 ? mapped[mapped.length - 1] : null;

    return (
        <PageLayout onLogout={onLogout} currentPage="reports/visit-tracker" onNavigate={onNavigate}>
            <div className="flex flex-col h-full overflow-hidden">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-3 shrink-0">
                    <div className="p-2 bg-emerald-50 rounded-lg">
                        <Map className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900">Visit Tracker</h1>
                        <p className="text-xs text-gray-500">Track employee visit routes on the map</p>
                    </div>
                </div>

                <div className="flex-1 flex overflow-hidden">
                    {/* Left Panel */}
                    <div className="w-80 flex flex-col border-r border-gray-200 bg-white overflow-hidden shrink-0">
                        {/* Filters */}
                        <div className="p-4 border-b border-gray-100 space-y-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <input type="date" value={date}
                                        onChange={e => { setDate(e.target.value); fetchReport(e.target.value, selectedEmp); }}
                                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                                <div className="relative">
                                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                    <select value={selectedEmp ?? ""}
                                        onChange={e => {
                                            const val = e.target.value ? Number(e.target.value) : null;
                                            setSelectedEmp(val);
                                            fetchReport(date, val);   // null = all employees
                                        }}
                                        className="w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent appearance-none bg-white">
                                        <option value="">All Employees</option>
                                        {employees.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                                    </select>
                                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                                </div>
                            </div>
                        </div>

                        {/* Stats */}
                        {data && (
                            <div className="grid grid-cols-2 gap-2 p-4 border-b border-gray-100">
                                <div className="bg-blue-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-blue-700">{data.total_visits}</p>
                                    <p className="text-xs text-blue-500">Total</p>
                                </div>
                                <div className="bg-emerald-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-emerald-700">{mapped.length}</p>
                                    <p className="text-xs text-emerald-500">Mapped</p>
                                </div>
                                <div className="bg-indigo-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-indigo-700">{data.doctor_visits}</p>
                                    <p className="text-xs text-indigo-500">Doctors</p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-3 text-center">
                                    <p className="text-xl font-bold text-purple-700">{data.firm_visits}</p>
                                    <p className="text-xs text-purple-500">Firms</p>
                                </div>
                            </div>
                        )}

                        {/* Visit Timeline */}
                        <div className="flex-1 overflow-y-auto">
                            {loading && (
                                <div className="flex items-center justify-center py-12">
                                    <span className="w-6 h-6 border-2 border-gray-200 border-t-emerald-500 rounded-full animate-spin" />
                                </div>
                            )}
                            {!loading && data && (data.visits ?? []).length === 0 && (
                                <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                                    <MapPin className="w-8 h-8 mb-2 opacity-30" />
                                    <p className="text-sm">No visits on this date</p>
                                </div>
                            )}
                            {!loading && data && (data.visits ?? []).length > 0 && (
                                <div className="p-4 space-y-1">
                                    {(data.visits ?? []).map((v, i) => {
                                        const isActive = activeVisit?.visit_id === v.visit_id;
                                        const hasPt = v.latitude != null && v.longitude != null;
                                        return (
                                            <button key={`${v.visit_type}-${v.visit_id}`}
                                                onClick={() => {
                                                    setActiveVisit(isActive ? null : v);
                                                    if (hasPt && mapRef) {
                                                        mapRef.panTo({ lat: v.latitude!, lng: v.longitude! });
                                                        mapRef.setZoom(15);
                                                    }
                                                }}
                                                className={`w-full text-left flex items-start gap-3 px-3 py-3 rounded-xl transition-all border ${isActive ? "bg-emerald-50 border-emerald-200" : "hover:bg-gray-50 border-transparent hover:border-gray-200"}`}>
                                                {/* Sequence badge */}
                                                <div className="shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white mt-0.5"
                                                    style={{ backgroundColor: visitColor(v.visit_type) }}>
                                                    {v.sequence ?? i + 1}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-1.5 flex-wrap">
                                                        <span className="text-sm font-medium text-gray-900 truncate">{v.entity_name}</span>
                                                        {v.visit_type === "doctor"
                                                            ? <Stethoscope className="w-3 h-3 text-blue-500 shrink-0" />
                                                            : <Building2 className="w-3 h-3 text-purple-500 shrink-0" />}
                                                    </div>
                                                    <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-500">
                                                        <Clock className="w-3 h-3" />
                                                        <span>{fmtTime(v.check_in)}</span>
                                                        {v.check_out && <><span>→</span><span>{fmtTime(v.check_out)}</span></>}
                                                    </div>
                                                    {v.check_in && !v.check_out
                                      ? <p className="text-xs text-green-500 mt-0.5">Open</p>
                                      : !hasPt && <p className="text-xs text-amber-500 mt-0.5">No GPS</p>
                                    }
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Legend */}
                        <div className="p-4 border-t border-gray-100 bg-gray-50">
                            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Legend</p>
                            <div className="space-y-1.5 text-xs text-gray-600">
                                <div className="flex items-center gap-2"><span className="w-3.5 h-3.5 rounded-full bg-blue-500" />Doctor Visit</div>
                                <div className="flex items-center gap-2"><span className="w-3.5 h-3.5 rounded-full bg-purple-500" />Firm Visit</div>
                                <div className="flex items-center gap-2"><span className="w-3.5 h-3.5 rounded-full border-2 border-emerald-500 bg-transparent" />Start</div>
                                <div className="flex items-center gap-2"><span className="w-3.5 h-3.5 rounded-full border-2 border-red-500 bg-transparent" />End</div>
                            </div>
                        </div>
                    </div>

                    {/* Map */}
                    <div className="flex-1 relative">
                        {!isLoaded ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <span className="w-8 h-8 border-2 border-gray-200 border-t-emerald-500 rounded-full animate-spin" />
                            </div>
                        ) : (
                            <GoogleMap
                                mapContainerStyle={MAP_STYLE}
                                center={DEFAULT_CENTER}
                                zoom={DEFAULT_ZOOM}
                                onLoad={onMapLoad}
                                options={{
                                    mapTypeControl: false,
                                    streetViewControl: false,
                                    fullscreenControl: true,
                                    zoomControl: true,
                                    styles: [{ featureType: "poi", elementType: "labels", stylers: [{ visibility: "off" }] }],
                                }}>
                                {/* Route Polyline */}
                                {polyline.length > 1 && (
                                    <Polyline
                                        path={polyline}
                                        options={{
                                            strokeColor: LINE_COLOR,
                                            strokeOpacity: 0.75,
                                            strokeWeight: 3,
                                            icons: [{
                                                icon: { path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW, scale: 3, strokeColor: "#fff", fillColor: LINE_COLOR, fillOpacity: 1 },
                                                offset: "50%",
                                                repeat: "120px",
                                            }],
                                        }}
                                    />
                                )}

                                {/* Visit Markers */}
                                {mapped.map((v, i) => {
                                    const isStart = i === 0;
                                    const isEnd = i === mapped.length - 1 && mapped.length > 1;
                                    const fill  = visitColor(v.visit_type);
                                    const ring  = isStart ? START_COLOR : isEnd ? END_COLOR : "#fff";
                                    const label = isStart ? "S" : isEnd ? "E" : String(v.sequence ?? i + 1);
                                    const size  = (isStart || isEnd) ? 48 : 40;
                                    return (
                                        <Marker
                                            key={`${v.visit_type}-${v.visit_id}`}
                                            position={{ lat: v.latitude!, lng: v.longitude! }}
                                            icon={{
                                                url: makePinSvg(label, fill, ring),
                                                scaledSize: new window.google.maps.Size(size, size + 10),
                                                anchor: new window.google.maps.Point(size / 2, size + 10),
                                            }}
                                            onClick={() => setActiveVisit(activeVisit?.visit_id === v.visit_id ? null : v)}
                                            zIndex={isStart || isEnd ? 99 : i + 1}
                                        />
                                    );
                                })}

                                {/* InfoWindow */}
                                {activeVisit && activeVisit.latitude != null && activeVisit.longitude != null && (
                                    <InfoWindow
                                        position={{ lat: activeVisit.latitude, lng: activeVisit.longitude }}
                                        onCloseClick={() => setActiveVisit(null)}>
                                        <div className="min-w-[180px] max-w-[240px] p-1">
                                            <div className="flex items-center gap-2 mb-2">
                                                <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: visitColor(activeVisit.visit_type) }} />
                                                <p className="font-semibold text-gray-900 text-sm leading-tight">{activeVisit.entity_name}</p>
                                            </div>
                                            {activeVisit.entity_code && <p className="text-xs text-gray-400 mb-1">{activeVisit.entity_code}</p>}
                                            <p className="text-xs text-gray-600 mb-1">{activeVisit.employee_name}</p>
                                            <div className="flex items-center gap-1 text-xs text-gray-500">
                                                <Clock className="w-3 h-3" />
                                                <span>{fmtTime(activeVisit.check_in)}</span>
                                                {activeVisit.check_out && <><span>→</span><span>{fmtTime(activeVisit.check_out)}</span></>}
                                            </div>
                                        </div>
                                    </InfoWindow>
                                )}
                            </GoogleMap>
                        )}

                        {/* No GPS overlay */}
                        {data && data.total_visits > 0 && mapped.length === 0 && (
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl px-6 py-5 flex flex-col items-center gap-2 border border-gray-200 pointer-events-auto">
                                    <Navigation className="w-8 h-8 text-amber-400" />
                                    <p className="font-semibold text-gray-800">No GPS data available</p>
                                    <p className="text-xs text-gray-500 text-center">Visits exist but no location data was recorded</p>
                                </div>
                            </div>
                        )}

                        {/* Empty state overlay */}
                        {!loading && !data && (
                            <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                                <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl px-6 py-5 flex flex-col items-center gap-2 border border-gray-200">
                                    <Map className="w-10 h-10 text-gray-300" />
                                    <p className="font-semibold text-gray-600">Select filters to view routes</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}
